import { useState } from "react";
import {
  Typography,
  TextField,
  Box,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Alert
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CouponTemplateFormDto,
  DesignType
} from "../../../store/models/coupon/CouponManagementDto";
import { validateImage, formatImageValidationErrors } from "../../../utils/imageValidation";

interface CouponDesignStepProps {
  formData: CouponTemplateFormDto;
  updateFormData: (updates: Partial<CouponTemplateFormDto>) => void;
  readOnly?: boolean;
}

const CouponDesignStep = ({ formData, updateFormData, readOnly = false }: CouponDesignStepProps) => {
  const { t } = useTranslation();
  const [imageValidationError, setImageValidationError] = useState<string>('');

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validationResult = await validateImage(file, undefined, t);

      if (validationResult.isValid) {
        updateFormData({ couponImage: file });
        setImageValidationError('');
      } else {
        setImageValidationError(formatImageValidationErrors(validationResult.errors));
        event.target.value = '';
      }
    }
  };

  const handleDesignTypeChange = (newType: DesignType) => {
    updateFormData({
      designType: newType,
      // Clear custom design fields if switching to NONE
      description: newType === DesignType.CUSTOM ? formData.description : undefined,
      couponImage: newType === DesignType.CUSTOM ? formData.couponImage : undefined,
      infoTextForEndUsers: newType === DesignType.CUSTOM ? formData.infoTextForEndUsers : undefined
    });
  };

  const renderErrorLines = (errorText: string) => {
    return errorText.split('\n').map((line) => (
      <div key={line}>{line}</div>
    ));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3859ff' }}>
        {t("coupon.form.couponDesign")}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Design Type Selection */}
        <Grid item xs={12}>
          <RadioGroup
            value={formData.designType}
            onChange={(e) => handleDesignTypeChange(e.target.value as DesignType)}
          >
            <FormControlLabel
              value={DesignType.NONE}
              control={<Radio sx={{ color: '#3859ff' }} disabled={readOnly} />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("coupon.form.designType.none")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("coupon.form.designType.noneDescription")}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              value={DesignType.CUSTOM}
              control={<Radio sx={{ color: '#3859ff' }} disabled={readOnly} />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("coupon.form.designType.custom")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("coupon.form.designType.customDescription")}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Grid>

        {/* Custom Design Configuration */}
        {formData.designType === DesignType.CUSTOM && (
          <>
            <Grid item xs={12}>
              <Box sx={{ pt: 2, borderTop: '1px solid #e5e7eb' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  {t("coupon.form.customDesignConfiguration")}
                </Typography>
              </Box>
            </Grid>

            {/* Two Column Layout: Input Fields (Left) and Preview (Right) */}
            <Grid item xs={12} md={6}>
              {/* Left Side - Input Fields */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* Brief Description */}
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t("coupon.form.description")}
                  value={formData.description || ''}
                  onChange={(e) => updateFormData({ description: e.target.value })}
                  placeholder="e.g., Get 20% off your entire purchase"
                  helperText={`${formData.description?.length || 0}/200 ${t("coupon.form.fieldHelpers.shortDescriptionVisible")}`}
                  inputProps={{ maxLength: 200 }}
                  sx={{ backgroundColor: 'white' }}
                  disabled={readOnly}
                />

                {/* Image Upload */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    {t("coupon.form.imageUpload.label")}
                  </Typography>
                  {imageValidationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {renderErrorLines(imageValidationError)}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ backgroundColor: '#3859ff', '&:hover': { backgroundColor: '#2847cc' }, mb: 1 }}
                    disabled={readOnly}
                  >
                    {t("coupon.form.imageUpload.uploadButton")}
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/png"
                      onChange={handleImageUpload}
                      disabled={readOnly}
                    />
                  </Button>
                  <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                    {t("coupon.form.imageUpload.requirements")}
                  </Typography>
                </Box>

                {/* Info Text For End Users */}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t("coupon.form.infoTextForEndUsers")}
                  value={formData.infoTextForEndUsers || ''}
                  onChange={(e) => updateFormData({ infoTextForEndUsers: e.target.value })}
                  placeholder="e.g., Offer valid only once per customer. Cannot be combined with other discounts."
                  helperText={`${formData.infoTextForEndUsers?.length || 0}/1224`}
                  inputProps={{ maxLength: 1224 }}
                  sx={{ backgroundColor: 'white' }}
                  disabled={readOnly}
                />
              </Box>
            </Grid>

            {/* Right Side - Coupon Preview */}
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'sticky' }}>
                <Box
                  sx={{
                    maxWidth: '400px',
                    mx: 'auto',
                    bgcolor: 'white',
                    borderRadius: 3,
                    boxShadow: 6,
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  {/* Image Area (1:1 Ratio) */}
                  <Box sx={{ position: 'relative', paddingTop: '100%', bgcolor: '#555' }}>
                    <Box
                      component="img"
                      src={
                        formData.couponImage
                          ? typeof formData.couponImage === 'string'
                            ? formData.couponImage
                            : URL.createObjectURL(formData.couponImage)
                          : 'https://placehold.co/650x650/3859ff/ffffff?text=Coupon+Image+650x650'
                      }
                      alt="Coupon Preview"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {/* Top Overlay for description */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        p: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          maxHeight: '48px',
                          overflow: 'hidden',
                          fontSize: '0.875rem'
                        }}
                      >
                        {formData.description || 'Brief description of the coupon offer'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Bottom Text Content */}
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formData.name || 'COUPON_NAME'}
                    </Typography>

                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {formData.infoTextForEndUsers || 'Info text for end users appears here.'}
                    </Typography>

                    <Box sx={{ pt: 1, borderTop: '1px dashed #e5e7eb' }}>
                      <Typography variant="caption" sx={{ color: '#3859ff', fontWeight: 'bold', fontSize: '0.75rem' }}>
                        {formData.validityType === 'DAYS_FROM_ACTIVATION' && formData.validityPeriodDays
                          ? `Expires after ${formData.validityPeriodDays} days`
                          : 'Expires: Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 1, maxWidth: '400px', mx: 'auto' }}>
                  This is a visual representation and may vary slightly in the final application.
                </Typography>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default CouponDesignStep;
